<script setup lang="ts">
import Module from '../../components/elements/Module.vue';
import { useAppStatus } from '../../composables/useAppStatus';

interface TrialStatusProps {
  id: string;
  onUpgrade: () => Promise<void>;
}
const props = defineProps<TrialStatusProps>();
const deploymentID = toRef(props, 'id');
const { deployment, isClovyrCode, isTrial, trialTimeRemaining, trialExpired, trialUpgradePrice } =
  useAppStatus(deploymentID);

const trialMessage = computed(() => {
  if (isTrial.value && trialExpired.value) {
    return `Your free trial has expired and the app will soon be paused. Love the app? Sign up and keep the fun going for just ${trialUpgradePrice.value}.`;
  }

  if (isTrial.value && !trialExpired.value) {
    return `You have ${trialTimeRemaining.value} remaining on your free trial. Loving the app? Upgrade and keep the fun going for just ${trialUpgradePrice.value}.`;
  }

  return '';
});

const parsedTimeRemaining = computed(() => Number(trialTimeRemaining));

const trialStyle = computed(() => {
  if (trialExpired.value || parsedTimeRemaining.value < 1) {
    return { backgroundColor: 'rgba(231, 107, 109, 0.9)' }; // red (error)
  }
  if (parsedTimeRemaining.value < 3) {
    return { backgroundColor: 'rgba(255, 230, 100, 0.9)' }; // yellow (warning)
  }
  return {};
});
</script>

<template>
  <Module
    class="module--trial-status trial-status"
    :bigHead="true"
    :hed="`${deployment.appName} Trial`"
    :style="trialStyle"
    v-if="isTrial"
  >
    <template #default>
      <div class="module__content">
        <div class="trial-status__content">
          <p class="trial-status__message">{{ trialMessage }}</p>
          <Button
            v-if="!isClovyrCode"
            label="Upgrade Now"
            class="upgrade"
            :reversed="true"
            @click="$emit('upgrade')"
          />
        </div>
      </div>
    </template>
  </Module>
</template>

<style scoped>
:deep(.module__head) {
  padding-bottom: 0.5rem;
}
:deep(.module__body) {
  padding-top: 0.5rem;
}

.module--trial-status {
  :deep(.module__content) {
    padding: 1rem;
  }

  .trial-status__content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap; /* allow wrapping by default */
  }

  .trial-status__message {
    flex: 1;
    margin: 0;
    font-size: 1rem;
    word-wrap: break-word;
  }

  :deep(.upgrade) {
    flex-shrink: 0;
    margin: 0;
  }

  /* Responsive behavior for screens < 1024px (typically "lg") */
  @media (max-width: 1300px) {
    .trial-status__content {
      flex-direction: column;
      align-items: flex-start;
    }

    :deep(.upgrade) {
      align-self: stretch; /* optional: make the button full width */
    }
  }
}
</style>
